/**
 * Shape-matched skeleton placeholders for high-traffic surfaces.
 *
 * The goal isn't a single generic spinner — it's to render the
 * *shape* of the final page so users feel orientation while the
 * data arrives. Each skeleton mirrors the corresponding component's
 * layout: same grid, same card heights, same column counts. When
 * the real content swaps in, the layout doesn't jump.
 *
 *   <KpiTilesSkeleton />        — 4-column gradient card grid
 *   <FoundryCardGridSkeleton />  — Foundry index grid of object cards
 *   <DataTableSkeleton rows={8}/> — generic table body
 *   <DashboardChartSkeleton />    — chart card with header + body
 */
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/** 4 KPI tiles in a responsive grid (matches data-health/KpiTiles). */
export function KpiTilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="overflow-hidden bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-3 w-28" />
              </div>
              <Skeleton className="h-6 w-6 rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Quick-stats strip — 6 small stat tiles (matches QuickStatsRow). */
export function QuickStatsRowSkeleton({ count = 6 }: { count?: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** A chart placeholder — title row + tall body. */
export function DashboardChartSkeleton({ height = 220 }: { height?: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-64" />
      </CardHeader>
      <CardContent>
        <Skeleton className="w-full" style={{ height }} />
      </CardContent>
    </Card>
  );
}

/** Generic table body — N rows × M columns. */
export function DataTableSkeleton({
  rows = 8,
  cols = 5,
  showHeader = true,
}: {
  rows?: number;
  cols?: number;
  showHeader?: boolean;
}) {
  return (
    <div className="w-full">
      {showHeader && (
        <div className="grid gap-3 border-b py-2.5 px-4 bg-muted/30"
             style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} className="h-3 w-2/3" />
          ))}
        </div>
      )}
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="grid gap-3 py-3 px-4"
               style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: cols }).map((__, c) => (
              <Skeleton key={c} className="h-3 w-3/4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Foundry index — grid of entity cards (matches the <ResourceCard/>
 *  anatomy used in pages/Foundry.tsx: icon tile, eyebrow + title +
 *  blurb, two meta cells, footer strip). */
export function FoundryCardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="flex flex-col">
          <div className="flex items-start gap-3 p-5 pb-4">
            <Skeleton className="h-9 w-9 rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-4 px-5 pb-5">
            <div className="space-y-1.5">
              <Skeleton className="h-2.5 w-14" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="space-y-1.5">
              <Skeleton className="h-2.5 w-14" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="mt-auto flex items-center justify-between border-t border-border bg-muted/40 px-5 py-3 rounded-b-lg">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-20 rounded-md" />
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Study catalog — grid of <ModuleTile/> launchers (icon tile, eyebrow,
 *  title, two-line blurb, "Run study" affordance). */
export function ModuleTileGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="flex min-h-[188px] flex-col p-5">
          <Skeleton className="h-11 w-11 rounded-lg" />
          <div className="mt-4 flex-1 space-y-2">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <div className="mt-4 flex justify-end">
            <Skeleton className="h-3 w-16" />
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Composite skeleton for the full Data Health dashboard layout. */
export function DataHealthDashboardSkeleton() {
  return (
    <div className="space-y-6">
      <KpiTilesSkeleton />
      <QuickStatsRowSkeleton />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardChartSkeleton />
        <DashboardChartSkeleton />
        <DashboardChartSkeleton />
        <DashboardChartSkeleton />
      </div>
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="p-0">
          <DataTableSkeleton rows={5} cols={6} />
        </CardContent>
      </Card>
    </div>
  );
}
